import { ProhibitedTerm, RejectionReason, Resource } from 'api'
import {
  ProgramType,
  ResourceStatus,
  resourceStatusLabels,
  ReviewStatus,
} from 'constants/resources'
import { ResourceState, ReviewedResource, ReviewFilter } from 'types'
import { filenameFromUrl } from './files'
import { hasSpecialChars, SpecialCharsRegex, stripHtml } from './strings'

type License = 'Pro' | 'Free' | 'Editorial'

const words = (str: unknown): string[] => {
  if (typeof str !== 'string') {
    return []
  }

  return str.trim().split(' ')
}

export const removeProhibitedTermsFromTitle = (
  title: string,
  prohibitedTerms: ProhibitedTerm[]
) => {
  let cleanedTitle = title
  prohibitedTerms.forEach(term => {
    const regex = new RegExp(`(?<![-\\w])${term.term}(?![-\\w])`, 'gi')
    cleanedTitle = cleanedTitle.replace(regex, '')
  })
  return cleanedTitle
}

export const removeProhibitedKeywords = (
  keywords: string[],
  prohibitedTerms: ProhibitedTerm[] | null | undefined
) => {
  if (!prohibitedTerms) return keywords

  return keywords.filter(keyword => {
    return !prohibitedTerms.some(prohibitedTerm => {
      const regex = new RegExp(
        '\\b' + escapeRegExp(prohibitedTerm.term) + '\\b',
        'i'
      )
      return regex.test(keyword)
    })
  })
}

const escapeRegExp = (str: string) => {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

export const isSubmittable = (
  resource: Resource,
  prohibitedTerms?: ProhibitedTerm[] | null
): boolean => {
  let keywords = resource.keywords
  let title = resource.title

  if (
    prohibitedTerms &&
    prohibitedTerms.length &&
    title &&
    keywords &&
    keywords.length
  ) {
    keywords = removeProhibitedKeywords(keywords, prohibitedTerms)
    title = removeProhibitedTermsFromTitle(title, prohibitedTerms)
  }

  return (
    (resource.program_type === ProgramType.WO ||
      Boolean(resource.license_set)) &&
    Boolean(resource.category) &&
    keywords.length >= 5 &&
    keywords.length <= 50 &&
    typeof title === 'string' &&
    title.trim().split(' ').length >= 3 &&
    title.length <= 200 &&
    !hasSpecialChars([title], SpecialCharsRegex.Loose) &&
    !hasSpecialChars(keywords)
  )
}

export const isBundleSubmittable = (resource: Resource): boolean =>
  typeof resource.description === 'string' &&
  stripHtml(resource.description).trim().length >= 300 &&
  !hasSpecialChars(
    [stripHtml(resource.description)],
    SpecialCharsRegex.Loose
  ) &&
  resource.previews.length >= 3

const isAcceptable = ({
  acceptable_as_free,
  acceptable_as_editorial,
}: Resource): boolean => acceptable_as_free || acceptable_as_editorial

const actionNeeded = (resource: Resource): boolean => {
  if (resource.state === 'needs_corrections') return true
  if (resource.state === 'started' && resource.comments_count > 0) return true

  return false
}

export const resourceTitle = (resource: Resource): string => {
  if (words(resource.title).length < 3) {
    return filenameFromUrl(resource.source_file?.url)
  }

  return resource.title || ''
}

export const resourceStatus = (resource: Resource): ResourceStatus => {
  if (actionNeeded(resource))
    return isAcceptable(resource)
      ? ResourceStatus.AcceptChanges
      : ResourceStatus.ActionNeeded
  if (resource.state === 'rejected') return ResourceStatus.Rejected
  if (resource.state === 'inactive') return ResourceStatus.Inactive
  if (!isSubmittable(resource)) return ResourceStatus.Processing
  if (resource.state === 'started') return ResourceStatus.Successful
  return ResourceStatus.Processing
}

export const isWO = (resource: Resource): boolean =>
  resource.program_type === ProgramType.WO

export const isEditorial = (resource: Resource): boolean =>
  resource.license === 'editorial'

export const isRejectAsEditorial = (resource?: ReviewedResource): boolean => {
  if (!resource) {
    return false
  }

  if (resource.status === ReviewStatus.Rejected) {
    return resource.rejectionReason.rejection_type === 'accept_as_editorial'
  }

  return false
}

export const getLicense = (resource: Resource): License => {
  if (resource.pro) {
    return 'Pro'
  }

  if (resource.license === 'editorial') {
    return 'Editorial'
  }

  return 'Free'
}

export const getStatusLabel = (
  state: ResourceState,
  reason?: string
): string =>
  state === 'active' && reason === 'publishing'
    ? resourceStatusLabels['exporting']
    : resourceStatusLabels[state]

export const isUploadable = (contentType: string): boolean => {
  const hiddenContentTypes = [
    'video_template',
    'brush',
    'background',
    'pattern',
    'texture',
    'icons',
  ]

  return !hiddenContentTypes.includes(contentType)
}

export const filterByRating =
  (filters: ReviewFilter[]) =>
  ({ status, rating }: ReviewedResource): boolean => {
    let result = false

    for (let i = 0; i < filters.length; i++) {
      const filter = filters[i]

      if (!result && typeof filter === 'number') {
        result = status === ReviewStatus.Approved && rating === filter
      }

      if (!result && filter === 'flagged') {
        result = status === ReviewStatus.Flagged
      }

      if (!result && filter === 'rejected') {
        result = status === ReviewStatus.Rejected
      }
    }

    return result
  }

export const filterRejectionReasonsFromCategory = (
  reasons: RejectionReason[],
  categoryIds: string[]
): { [key: string]: RejectionReason[] } => {
  const result: { [key: string]: RejectionReason[] } = {}

  if (reasons && categoryIds) {
    categoryIds.forEach(categoryId => {
      result[categoryId] = reasons.filter(({ categories }) =>
        Boolean(categories?.some(cat => cat.id === categoryId))
      )
    })
  }

  return result
}

export const filterRejectionReasonsFromResources = (
  reasons: RejectionReason[] | null,
  resources: Resource[]
): RejectionReason[] => {
  if (!reasons || !resources) {
    return []
  }

  const result: RejectionReason[] = resources.reduce((acc, resource) => {
    let filtered: RejectionReason[]

    if (resource.category?.id) {
      filtered = reasons.filter(({ categories }) =>
        Boolean(categories?.some(cat => cat.id === resource.category?.id))
      )
    } else {
      filtered = reasons.filter(
        ({ content_type }) => resource.content_type === content_type
      )
    }

    filtered.forEach(reason => {
      const idx = acc.findIndex(x => x.id === reason.id)

      if (idx === -1) {
        acc.push(reason)
      }
    })

    return acc
  }, [] as RejectionReason[])

  return result.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
}
